// src/components/admin/UserManagement.jsx
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useUsers } from '../../hooks/useUsers';
import { useAuth } from '../../context/AuthContext';
import UserForm from './UserForm';
import {
  Button,
  Cell,
  EmptyState,
  ErrorState,
  IconButton,
  Modal,
  PageHeader,
  Panel,
  Pill,
  Row,
  RowActions,
  SearchInput,
  Spinner,
  Table,
} from '../common/ui';

const initials = (name = '') =>
  name.replace(/[^a-zA-Z0-9]/g, ' ').trim().slice(0, 2).toUpperCase() || '?';

// authProvider is 'local' | 'microsoft' | 'both'
const SIGN_IN = {
  local: { label: 'Password', tone: 'neutral' },
  microsoft: { label: 'Microsoft', tone: 'ok' },
  both: { label: 'Both', tone: 'ok' },
};

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const UserManagement = () => {
  const { users, isLoading, error, refetch, createUser, updateUser, deleteUser } = useUsers();
  const { user: currentUser } = useAuth();
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) =>
      [user.username, user.email, user.role?.name].filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [users, query]);

  const handleSubmit = async (data) => {
    setIsSaving(true);
    try {
      if (editing._id) {
        await updateUser({ userId: editing._id, userData: data });
        toast.success(`${data.username} updated`);
      } else {
        await createUser(data);
        toast.success(`${data.username} added`);
      }
      setEditing(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete "${user.username}"? They lose access immediately.`)) return;
    try {
      await deleteUser(user._id);
      toast.success(`${user.username} deleted`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete that person');
    }
  };

  return (
    <div>
      <PageHeader
        title="People"
        subtitle="Accounts are invite-only — someone must exist here before Microsoft sign-in will let them in."
      >
        <Button variant="primary" onClick={() => setEditing({})}>
          <Plus className="h-4 w-4" />
          Add person
        </Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or role…"
          aria-label="Search people"
          className="max-w-xs"
        />
        <span className="tnum font-mono text-xs text-ink-3">
          {filtered.length} of {users.length}
        </span>
      </div>

      {isLoading ? (
        <Spinner label="Loading people…" />
      ) : error ? (
        <ErrorState title="Couldn't load people" onRetry={refetch}>
          {error.message}
        </ErrorState>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Users} title={`Nobody matches “${query.trim()}”`}>
          Try a shorter word, or search by email domain.
        </EmptyState>
      ) : (
        <Panel>
          <Table head={['Person', 'Email', 'Role', 'Signs in with', 'Added', '']}>
            {filtered.map((user) => {
              const signIn = SIGN_IN[user.authProvider] || SIGN_IN.local;
              const isSelf = user._id === currentUser.id;

              return (
                <Row key={user._id}>
                  <Cell>
                    <div className="flex items-center gap-3">
                      <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
                        {initials(user.username)}
                      </span>
                      <span className="font-medium">{user.username}</span>
                      {isSelf && <Pill>You</Pill>}
                    </div>
                  </Cell>

                  <Cell className="text-ink-2">{user.email || '—'}</Cell>

                  <Cell>
                    <Pill
                      tone={user.role?.name === 'admin' ? 'accent' : 'neutral'}
                      className="capitalize"
                    >
                      {user.role?.name || 'No role'}
                    </Pill>
                  </Cell>

                  <Cell>
                    <Pill tone={signIn.tone}>{signIn.label}</Pill>
                  </Cell>

                  <Cell className="tnum whitespace-nowrap text-ink-3">
                    {formatDate(user.createdAt)}
                  </Cell>

                  <Cell align="right">
                    <RowActions>
                      <IconButton label={`Edit ${user.username}`} onClick={() => setEditing(user)}>
                        <Pencil className="h-4 w-4" />
                      </IconButton>
                      <IconButton
                        label={
                          isSelf ? 'You can’t delete your own account' : `Delete ${user.username}`
                        }
                        tone="danger"
                        disabled={isSelf}
                        onClick={() => handleDelete(user)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    </RowActions>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        </Panel>
      )}

      {editing && (
        <Modal
          title={editing._id ? `Edit ${editing.username}` : 'Add a person'}
          description={
            editing._id
              ? 'Changing their role changes which tools they see.'
              : 'They’ll be able to sign in as soon as you save.'
          }
          onClose={() => setEditing(null)}
        >
          <UserForm
            user={editing}
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      )}
    </div>
  );
};

export default UserManagement;
